export interface Transcription {
  results: {
    audio_segments: TranscriptionSegment[];
  };
}

export interface TranscriptionSegment {
  id: number;
  transcript: string;
  start_time: string;
  end_time: string;
  speaker_label: string;
}

export class TranscriptionReader {
  private _blocks: Record<number, TranscriptionBlock> = {};

  public constructor(transcript: Transcription) {
    this.process(transcript);
  }

  private process(transcript: Transcription): void {
    for (const segment of transcript.results.audio_segments) {
      this._blocks[segment.id] = new TranscriptionBlock(
        segment.id,
        Number(segment.start_time ?? 0),
        Number(segment.end_time ?? 0),
        segment.transcript,
        segment.speaker_label
      );
    }
  }

  public getBlockAtTimeSeconds(seconds: number): TranscriptionBlock | null {
    const sortedBlocks = this.blocks;

    for (let i = 0; i < sortedBlocks.length; i++) {
      const block = sortedBlocks[i];
      const nextBlock = sortedBlocks[i + 1];

      // Use the minimum of block's end time or next block's start time as the boundary
      // This prevents skipping short blocks while also respecting actual silence/pauses
      const endBoundary = nextBlock
        ? Math.min(block.endSeconds, nextBlock.startSeconds)
        : block.endSeconds;

      if (seconds >= block.startSeconds && seconds < endBoundary) {
        return block;
      }
    }
    return null;
  }

  public getTimeOfBlock(blockId: number): number | null {
    if (blockId in this._blocks) {
      return this._blocks[blockId].startSeconds;
    }
    return null;
  }

  public get blocks(): TranscriptionBlock[] {
    return Object.values(this._blocks).sort(
      (a, b) => a.startSeconds - b.startSeconds
    );
  }
}

export class TranscriptionBlock {
  public readonly id: number;
  public readonly startSeconds: number;
  public readonly endSeconds: number;
  public readonly text: string;
  private readonly _speakerLabel: string;

  constructor(
    id: number,
    startSeconds: number,
    endSeconds: number,
    text: string,
    speakerLabel: string
  ) {
    this.id = id;
    this.startSeconds = startSeconds;
    this.endSeconds = endSeconds;
    this.text = text;
    this._speakerLabel = speakerLabel;
  }

  public get speakerLabel(): string {
    const [, speakerId] = this._speakerLabel?.split("_") ?? [];
    return speakerId === "0" ? "Agent" : "Customer";
  }
}
